import React, { useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  append,
  mergeDeepRight,
  mergeRight,
  propEq,
  reject,
  remove,
} from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import MediumFormatsDialog from "../edit/MediumFormatsDialog";
import EditIcon from "@mui/icons-material/Edit";
import { caseInsensitiveSort } from "../../util/sorts";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const sortMediumFormats = caseInsensitiveSort("display");

const MediumFormatsCard = ({ submissionType, project, loading, onUpdate }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const updatedSubType = mergeRight(submissionType, {
        mediumFormats: data,
      });
      const update = mergeDeepRight(project, {
        submissionTypes: append(
          updatedSubType,
          reject(propEq("name", submissionType.name), project.submissionTypes),
        ),
      });
      console.log(update);
      onUpdate(update);
    },
    [submissionType, project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const updatedSubType = mergeRight(submissionType, {
        mediumFormats: remove(
          idx,
          1,
          sortMediumFormats(submissionType.mediumFormats),
        ),
      });
      const update = mergeDeepRight(project, {
        submissionTypes: append(
          updatedSubType,
          reject(propEq("name", submissionType.name), project.submissionTypes),
        ),
      });
      console.log(update);
      onUpdate(update);
    },
    [project, submissionType, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
          color: "primary",
        }}
        title={`${submissionType.displayName} Formats`}
        action={
          <Tooltip title={`Add ${submissionType.displayName} Formats`}>
            <IconButton onClick={handleAddClick} size="large">
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(submissionType) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Formats.
          </Box>
        )}
        {isBlank(submissionType.mediumFormats) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Formats set up for this Submission Type.
          </Box>
        )}
        {notBlank(submissionType.mediumFormats) && (
          <List dense style={{ maxHeight: 160, overflowY: "auto" }}>
            {mapIndexed(
              (format, idx) => (
                <ListItem key={format.name}>
                  <ListItemText primary={format.display} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(idx)}
                      dialogTitle="Remove Medium Format"
                      dialogBody="Are you sure you want to remove this medium format?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              sortMediumFormats(submissionType.mediumFormats),
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && (
        <MediumFormatsDialog
          submissionType={submissionType}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default MediumFormatsCard;
